import type { TableBilling } from "~/interface/TableBilling";
import { roundMoney } from "~/utils/money";

/**
 * Pre-VAT base for an On-The-House / 100% comp bill.
 *
 * The additional discount stored in `dc2[2]` is applied PRE-VAT (see
 * ReceiptOrder.applyBilling: BeforeVatV2 = SubTotal - ItemDiscount - Discount +
 * ServiceCharge + CardCharge). To fully comp a bill we need BeforeVatV2 = 0, so
 * `dc2[2]` must equal the exact pre-VAT base — NOT the VAT-inclusive grand total
 * (which was the bug: it drove the pre-VAT base negative and broke reports).
 *
 * Canonical formula (must match the server exactly):
 *   preVatBase = SubTotal - ItemDiscount + ServiceCharge
 *
 * `billing.subTotal` is already NET of item discount (sum of discountedPrice,
 * see calculateBilling), i.e. (SubTotal - ItemDiscount), so we add the service
 * charge amount directly. Compute from the pre-VAT components, never from the
 * already-rounded grand total (that introduces ~0.12 drift).
 *
 * Card charge is intentionally excluded: an On-The-House bill is paid with 0, so
 * its card charge is zeroed (see applyOnTheHouseBilling), which keeps
 * BeforeVatV2 = 0 for the comp.
 */
export function computeOnTheHousePreVatBase(billing: Partial<TableBilling> = {}): number {
  const b = billing || {}
  const netSubTotal = Number(b.subTotal) || 0
  const serviceCharge = Array.isArray(b.sc) ? (Number(b.sc[2]) || 0) : 0
  return roundMoney(netSubTotal + serviceCharge)
}
